import { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Paper, Stack, Typography,
} from "@mui/material";
import {
  getCourseAudioReadiness, previewCourseAudioAssignments, commitCourseAudioAssignments,
} from "../service/service";
import VoiceActorSelector from "./VoiceActorSelector";

const requestMessage = (error, fallback) =>
  error.response?.data?.message || error.response?.data?.err || fallback;

const AudioAssignmentPanel = ({ course, onCourseChange, refreshKey }) => {
  const [readiness, setReadiness] = useState(null);
  const [voice, setVoice] = useState("");
  const [selected, setSelected] = useState([]);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const refresh = useCallback(async () => {
    try {
      const result = await getCourseAudioReadiness(course._id);
      setReadiness(result.data);
      setSelected([]);
      setError("");
    } catch {
      setError("Could not check audio assignments.");
    }
  }, [course._id]);
  useEffect(() => { refresh(); }, [refresh, refreshKey, course.voices]);

  const prepare = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await previewCourseAudioAssignments(course._id, voice, selected);
      setPreview(result.data);
    } catch (requestError) {
      setError(requestMessage(requestError, "Could not preview assignments. Refresh and try again."));
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await commitCourseAudioAssignments(course._id, preview);
      setNotice(`${data.assigned.length} assigned, ${data.skipped.length} already assigned, ${data.conflicts.length} changed, ${data.failed.length} failed.`);
      setPreview(null);
      await refresh();
      if (data.conflicts.length || data.failed.length) {
        setError("Some recordings changed or could not be assigned. Review the refreshed list and preview again.");
      } else {
        setOpen(false);
      }
    } catch (requestError) {
      setError(requestMessage(requestError, "Assignments could not be saved. Refresh and preview again."));
    } finally {
      setBusy(false);
    }
  };

  const close = () => { setOpen(false); setPreview(null); };
  const unassigned = readiness?.unassigned || [];

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="h6">Audio assignments</Typography>
      <Typography sx={{ my: 1 }}>
        {readiness
          ? `${readiness.assigned} of ${readiness.total} audio roles assigned to registered voice actors.`
          : "Checking audio assignments…"}
      </Typography>
      {readiness?.ready && (
        <Alert severity="success">Audio assignments are ready. Character artwork is optional.</Alert>
      )}
      {!!readiness?.issues.length && (
        <>
          <Alert severity="warning">
            {readiness.issues.length} audio issue(s) must be resolved before publishing or activating.
            Open the question to upload missing audio, or assign existing recordings below.
          </Alert>
          <Box component="ul" sx={{ maxHeight: 160, overflow: "auto" }}>
            {readiness.issues.map((issue, index) => (
              <li key={`${issue.questionId}-${index}`}>{issue.message}</li>
            ))}
          </Box>
        </>
      )}
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button disabled={!unassigned.length}
          onClick={() => { setOpen(true); setPreview(null); setNotice(""); }}>
          Assign existing audio ({unassigned.length})
        </Button>
        <Button disabled={busy} onClick={refresh}>Refresh</Button>
      </Stack>
      {notice && <Alert severity="info">{notice}</Alert>}
      {error && !open && <Alert severity="error">{error}</Alert>}

      <Dialog open={open} onClose={() => !busy && close()} fullWidth maxWidth="md">
        <DialogTitle>{preview ? "Confirm audio assignments" : "Assign existing audio"}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Existing files and fallback URLs stay in place. Only unassigned recordings
            are linked to the selected actor.
          </Typography>
          {!preview && (
            <VoiceActorSelector courseId={course._id} course={course} value={voice}
              onChange={setVoice} onCourseChange={onCourseChange} disabled={busy} />
          )}
          {preview ? (
            <>
              <Typography sx={{ mt: 2 }}>Voice actor: <strong>{preview.voiceName}</strong></Typography>
              <Box component="ul">
                {preview.records.map(record => <li key={record.id}>{record.label}</li>)}
              </Box>
            </>
          ) : (
            <>
              <FormControlLabel label="Select all unassigned recordings" control={
                <Checkbox disabled={busy}
                  checked={!!unassigned.length && selected.length === unassigned.length}
                  onChange={event => setSelected(event.target.checked ? unassigned.map(record => record.id) : [])} />
              } />
              <Stack>
                {unassigned.map(record => (
                  <FormControlLabel key={record.id} label={record.label} control={
                    <Checkbox disabled={busy} checked={selected.includes(record.id)}
                      onChange={event => setSelected(current => event.target.checked
                        ? [...current, record.id] : current.filter(id => id !== record.id))} />
                  } />
                ))}
              </Stack>
            </>
          )}
          {notice && <Alert severity="info">{notice}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={close}>Close</Button>
          {preview ? (
            <>
              <Button disabled={busy} onClick={() => setPreview(null)}>Back</Button>
              <Button disabled={busy} variant="contained" onClick={commit}>Confirm assignments</Button>
            </>
          ) : (
            <Button disabled={busy || !voice || !selected.length}
              variant="contained" onClick={prepare}>Preview assignments</Button>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
export default AudioAssignmentPanel;
